import { Separator, HStack, VStack } from '@web/components/atoms/Layout'
import { DonorPaysCheckbox, TotalAmount, useDonationFormContext } from '@web/components/page-components/DonationForm'
import { PageButton } from '@web/components/page-components/PageButton'
import { DonationFrequency, PlatformFeeType } from '@web/graphql-components'
import { usePaymentStore } from '../../../libs/hooks/usePaymentStore'
import QuickPayButton from './QuickPayButton'
import StripeWrapper from './StripeWrapper'
import PayPalButton from './PayPalButton'
import { useTotalAmount } from '@web/components/page-components/DonationForm/donationUtils'
import { Body2, Caption } from '@web/components/atoms/Typography'
import { useState } from 'react'
import { PaymentMethodType } from '@web/graphql-queries/graphql'
import { useShallow } from 'zustand/react/shallow'
import { Alert } from '@web/components/atoms/Alert'
import { Skeleton } from '@web/components/atoms/Skeleton'
import { useDonorPaysAmount, useHandlePaymentIntent } from './utils'
import { Collapsible } from '@web/components/atoms/Transitions'
import ErrorBoundary from '@web/components/molecules/ErrorBoundary'
import { useHttpClient } from 'apps/fe-donation/libs/hooks/useHttpClient'

type PaymentButtonsProps = { expressTitle?: string; validateForm?: boolean }

const PaymentButtons = ({ expressTitle, validateForm = true }: PaymentButtonsProps) => {
  const [error, setError] = useState('')
  const formValues = useDonationFormContext((state) => state.formValues)
  const hasErrors = useDonationFormContext(useShallow(({ errors }) => Object.keys(errors).length > 0))
  const actions = useDonationFormContext((state) => state.actions)
  const showDirectDebit = useDonationFormContext(
    (state) =>
      state.settings.styles?.options?.hasDirectDebit && state.formValues.givingFrequency !== DonationFrequency.OneTime,
  )

  const { givingFrequency = DonationFrequency.OneTime } = formValues
  const { nextStep } = actions

  const paymentSettings = usePaymentStore((state) => state.paymentSettings)
  const paymentProviderSettings = paymentSettings?.donationPagePaymentProviderSettings
  const donorPaysPlatformFees = paymentSettings?.donorPaysPlatformFees
  const isDonorPaysActive = paymentSettings?.platformFeeType === PlatformFeeType.DonorPays

  const hasLoaded = usePaymentStore((state) => state.hasLoaded)
  const totalAmount = Math.floor(useTotalAmount() * 100)
  const { donorPaysAmountInDollars } = useDonorPaysAmount()
  const [isLoadingPaymentMethodType, setIsLoadingPaymentMethodType] = useState<PaymentMethodType | null>(null)

  const paymentIntentHandler = useHandlePaymentIntent((error) => setError(error))

  const handlePaymentIntent = async (paymentMethodType: PaymentMethodType) => {
    setIsLoadingPaymentMethodType(paymentMethodType)
    return await paymentIntentHandler.handlePaymentIntent(paymentMethodType)
  }

  const hasPayPalSettings =
    paymentProviderSettings?.payPalMerchantSetting &&
    paymentProviderSettings?.payPalMerchantSetting.merchantEmail &&
    paymentProviderSettings?.payPalMerchantSetting.merchantId
  const hasPaymentSettings = paymentProviderSettings?.stripePaymentSettings || hasPayPalSettings
  const isLoading = paymentIntentHandler.isLoading
  const [hasQuickPayLoaded, setHasQuickPayLoaded] = useState(false)
  const onReady = () => setHasQuickPayLoaded(true)
  const httpClient = useHttpClient({ isRelativeUrl: true })

  if (!hasLoaded) {
    return <Skeleton w="100%" height="40px" />
  }

  return (
    <>
      <Collapsible.Root open={totalAmount > 0} w="100%">
        <Collapsible.Content>
          <VStack gap="16px" w="100%">
            {isDonorPaysActive && (donorPaysPlatformFees?.length ?? 0) > 0 && (
              <DonorPaysCheckbox donorPaysAmount={donorPaysAmountInDollars} />
            )}
            <TotalAmount donorPaysAmount={donorPaysAmountInDollars} />
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
      {hasPaymentSettings ? (
        <>
          {givingFrequency === DonationFrequency.OneTime && (
            <>
              {expressTitle && hasPaymentSettings && (
                <Body2 as="strong" fontWeight="700" mb="4">
                  {expressTitle}
                </Body2>
              )}
              <StripeWrapper>
                <QuickPayButton onReady={onReady} handlePaymentIntent={handlePaymentIntent} />
              </StripeWrapper>
              <ErrorBoundary
                apiPrefix="/api/"
                httpClient={httpClient}
                fallback={<>{/* just swallow it and console the error */}</>}
              >
                <PayPalButton onReady={onReady} />
              </ErrorBoundary>
              {hasQuickPayLoaded && paymentProviderSettings?.stripePaymentSettings && (
                <HStack w="100%">
                  <Separator variant="dashed" w="100%" borderColor="ge.text.primary" />
                  <Body2 as="strong" fontWeight="700">
                    OR
                  </Body2>
                  <Separator variant="dashed" w="100%" borderColor="ge.text.primary" />
                </HStack>
              )}
            </>
          )}
          {paymentProviderSettings.stripePaymentSettings && (
            <>
              <PageButton
                data-testid="donate-credit-card"
                buttonText={
                  givingFrequency === DonationFrequency.OneTime || showDirectDebit ? 'Donate by Card' : 'Next'
                }
                name="Primary"
                width="100%"
                isLoading={isLoading && isLoadingPaymentMethodType === PaymentMethodType.CreditCard}
                disabled={isLoading}
                onClick={async () => {
                  if (validateForm && (hasErrors || !actions.validateForm(true))) {
                    return
                  }

                  if (await handlePaymentIntent(PaymentMethodType.CreditCard)) {
                    nextStep()

                    return
                  }
                }}
              />
              {showDirectDebit && (
                <PageButton
                  buttonText="Donate by Direct Debit"
                  name="Primary"
                  width="100%"
                  isLoading={isLoading && isLoadingPaymentMethodType === PaymentMethodType.DirectDebit}
                  disabled={isLoading}
                  onClick={async () => {
                    if (validateForm && (hasErrors || !actions.validateForm(true))) {
                      return
                    }

                    if (await handlePaymentIntent(PaymentMethodType.DirectDebit)) {
                      nextStep()

                      return
                    }
                  }}
                />
              )}
            </>
          )}
          {error && <Caption color="red">{error}</Caption>}
        </>
      ) : (
        <Alert status="warning" color="ge.text.primary">
          Payments are disabled, please setup your payment gateway.
        </Alert>
      )}
    </>
  )
}

export default PaymentButtons
